import { DOCUMENT } from '@angular/common';
import { inject, Injectable, Type } from '@angular/core';
import {
    ConfirmationDialogComponent,
    IConfirmationDialog,
} from '@app/components/_ui/confirmation-dialog';
import { CustomDialogCloseIconComponent } from '@app/components/_ui/custom-dialog/custom-dialog-close-icon.component';
import { CustomDialogHeaderComponent } from '@app/components/_ui/custom-dialog/custom-dialog-header.component';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { lastValueFrom } from 'rxjs';

const SCROLL_BLOCK_CLASS = 'p-overflow-hidden';

@Injectable({
    providedIn: 'root',
})
export class CustomDialogService {
    private dialogService: DialogService = inject(DialogService);
    private document = inject(DOCUMENT);
    private openDialogs: DynamicDialogRef[] = [];

    open<D, T = any>(component: Type<T>, config?: DynamicDialogConfig, data?: D) {
        const dialogRef = this.dialogService.open(component, {
            ...config,
            data: data,
            draggable: false,
            resizable: false,
            maskStyleClass: config?.dismissableMask ? '' : 'backdrop-blur-sm',
            modal: true,
            templates: {
                header: CustomDialogHeaderComponent,
                closeicon: CustomDialogCloseIconComponent,
            },
        })!;
        this.openDialogs.push(dialogRef);
        dialogRef.onClose.subscribe(() => this.onDialogClosed(dialogRef));
        dialogRef.onDestroy.subscribe(() => this.onDialogClosed(dialogRef));
        return dialogRef;
    }

    async confirm(config?: DynamicDialogConfig, data?: IConfirmationDialog) {
        return await lastValueFrom(
            this.open<IConfirmationDialog>(
                ConfirmationDialogComponent,
                { width: '350px', ...config },
                data,
            ).onClose,
        );
    }

    closeAll() {
        this.openDialogs.forEach(dialogRef => {
            dialogRef.close();
        });
        this.openDialogs = [];
    }

    /**
     * PrimeNG releases the body scroll block whenever any dialog closes, even when another
     * modal is still open. Restore it so closing a nested dialog does not bring the page
     * scrollbar back underneath the dialog that stays open.
     */
    private onDialogClosed(dialogRef: DynamicDialogRef): void {
        this.openDialogs = this.openDialogs.filter(open => open !== dialogRef);
        if (!this.openDialogs.length) return;

        const body = this.document.body;
        const scrollbarWidth = body.style.getPropertyValue('--p-scrollbar-width');
        setTimeout(() => {
            if (!this.openDialogs.length) return;
            body.classList.add(SCROLL_BLOCK_CLASS);
            if (scrollbarWidth) body.style.setProperty('--p-scrollbar-width', scrollbarWidth);
        });
    }
}
